import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { CancelClientMarketOrderMutationRequest, CancelClientMarketOrderMutationResponse, CancelClientMarketOrderPathParams, CancelClientMarketOrder404 } from "../types/CancelClientMarketOrder";
import type { UseMutationOptions } from "@tanstack/react-query";

 type CancelClientMarketOrderClient = typeof client<CancelClientMarketOrderMutationResponse, CancelClientMarketOrder404, CancelClientMarketOrderMutationRequest>;
type CancelClientMarketOrder = {
    data: CancelClientMarketOrderMutationResponse;
    error: CancelClientMarketOrder404;
    request: CancelClientMarketOrderMutationRequest;
    pathParams: CancelClientMarketOrderPathParams;
    queryParams: never;
    headerParams: never;
    response: CancelClientMarketOrderMutationResponse;
    client: {
        parameters: Partial<Parameters<CancelClientMarketOrderClient>[0]>;
        return: Awaited<ReturnType<CancelClientMarketOrderClient>>;
    };
};
/**
 * @description Cancels an existing order for a given client market. Similar to amending
an order, this route requires the original client order reference to be
provided plus a new client order reference.
 * @summary Cancel
 * @link /api/clients/:clientCode/markets/:marketCode/orders/:originalClientOrderRef/:clientOrderRef
 */
export function useCancelClientMarketOrder(clientCode: CancelClientMarketOrderPathParams["clientCode"], marketCode: CancelClientMarketOrderPathParams["marketCode"], originalClientOrderRef: CancelClientMarketOrderPathParams["originalClientOrderRef"], clientOrderRef: CancelClientMarketOrderPathParams["clientOrderRef"], options: {
    mutation?: UseMutationOptions<CancelClientMarketOrder["response"], CancelClientMarketOrder["error"], CancelClientMarketOrder["request"]>;
    client?: CancelClientMarketOrder["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<CancelClientMarketOrder["data"], CancelClientMarketOrder["error"], CancelClientMarketOrder["request"]>({
                method: "delete",
                url: `/api/clients/${clientCode}/markets/${marketCode}/orders/${originalClientOrderRef}/${clientOrderRef}`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}